import { useTranslation } from '@gimlite/common';
import { useStoreUIV1, Wizard } from '@gimlite/ui-v1';
import { observer } from 'mobx-react-lite';
import { ArticleCode, ContractEntity } from '../../client/graphql';
import { DefaultContractBaseWizard } from '../../common/defaultValue/wizard';
import { CredentialType } from '../../common/mapper/credential.mapper';
import { ContractBaseFormType } from '../../form/contract-create/contract-create.form';
import { ContractsCreateFormType } from '../../form/contracts-create/contracts-create.form';
import { ProductFormType } from '../../form/product/product.form';
import { ConfirmationStep } from './steps/confirmation/confirmation.step';
import { ContractStep } from './steps/contract/contract.step';
import { ParkersStep } from './steps/parkers/parkers.step';
import { ProductStep } from './steps/product/product.step';
import { SummaryStep } from './steps/summary/summary.step';
import { TenantStep } from './steps/tenant/tenant.step';

export type CreateNewMasterPoolContractWizardContext = {
  parkingId: string | null;
  product: {
    id: ProductFormType.Data.ProductId | null;
    period: number | null;
    credentials: CredentialType.GlobalName[];
    articleCodes: Array<ArticleCode>;
  };
  contractBase: ContractBaseFormType.Data.ContractBase;
  tenant: {
    name1: string | null;
    name2: string | null;
    address1: string | null;
    address2: string | null;
    zipCode: string | null;
    city: string | null;
    country: string | null;
    hasAdminPool: boolean;
    canCreateSubContract: boolean;
  };
  poolAdmin: {
    username: string | null;
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
    lang: string | null;
  };
  contracts: {
    created: ContractsCreateFormType.Data.Created[];
    checked: ContractEntity[];
  };
  contractMode: 'create' | 'exist';
  contractExistedSave: ContractEntity[];
};

export const CreateNewMasterPoolContractWizard = observer(() => {
  const { t, lang } = useTranslation();
  const { WizardStore } = useStoreUIV1();

  const { parkingId } = WizardStore?.selected?.store;

  return (
    <Wizard<CreateNewMasterPoolContractWizardContext>
      config={{
        title: t(`createANewMasterPoolContract`),
      }}
      data={{
        defaultContext: {
          parkingId,
          product: {
            id: null,
            period: null,
            credentials: [],
            articleCodes: [],
          },
          contractBase: DefaultContractBaseWizard({
            t,
            lang,
          }),
          tenant: {
            name1: null,
            name2: null,
            address1: null,
            address2: null,
            city: null,
            country: 'CHE',
            zipCode: null,
            hasAdminPool: false,
            canCreateSubContract: false,
          },
          poolAdmin: {
            firstName: null,
            lastName: null,
            lang: null,
            phone: null,
            username: null,
          },
          contracts: {
            checked: [],
            created: [],
          },
          contractMode: 'create',
          contractExistedSave: [],
        },
      }}
      handleEvent={{
        done: async (data) => {
          console.log('done', { data });
        },
        clear: () => {
          console.log('CLEAR');
        },
        close: () => {
          WizardStore.clear();
        },
      }}
    >
      <Wizard.Item config={{ title: t('product'), component: ProductStep }} />
      <Wizard.Item config={{ title: t('contract'), component: ContractStep }} />
      <Wizard.Item config={{ title: t('tenant'), component: TenantStep }} />
      <Wizard.Item config={{ title: t('parkers'), component: ParkersStep }} />
      <Wizard.Item
        config={{ title: t('confirmation'), component: ConfirmationStep }}
      />
      <Wizard.Item config={{ title: t('summary'), component: SummaryStep }} />
    </Wizard>
  );
});
